import { type MetaFunction } from "react-router"
import {
	TypographyH1,
	TypographyH2,
	TypographyP,
} from "#app/components/ui/typography.tsx"
import { APP_NAME } from "#app/utils/constants"
import { generateMeta } from "#app/utils/meta"

export const meta: MetaFunction = () => {
	return generateMeta({
		title: `Política de Devoluciones - ${APP_NAME}`,
		description:
			"Política de devoluciones de Greta's Patchwork. Información sobre cómo devolver productos, plazos, condiciones y proceso de reembolso.",
		canonical: "/return-policy",
	})
}

export default function ReturnPolicyRoute() {
	return (
		<main className="container h-full py-12">
			<div className="mx-auto max-w-3xl">
				<TypographyH1>Política de Devoluciones</TypographyH1>

				<TypographyP className="mt-6">
					En {APP_NAME}, nos esforzamos por ofrecer productos de patchwork
					hechos a mano de la más alta calidad. Queremos que estés completamente
					satisfeito/a con tu compra, pero entendemos que a veces puede ser
					necesario devolver un producto.
				</TypographyP>

				<TypographyH2 className="mt-8">Plazo de Devolución</TypographyH2>
				<TypographyP>
					Dispones de un plazo de <strong>14 días naturales</strong> desde la
					recepción del producto para ejercer tu derecho de devolución.
				</TypographyP>

				<TypographyH2 className="mt-8">Condiciones del Producto</TypographyH2>
				<TypographyP>
					Para que una devolución sea aceptada, el producto debe cumplir las
					siguientes condiciones:
				</TypographyP>
				<ul className="my-6 ml-6 list-disc space-y-2">
					<li>El producto debe estar sin usar y en su estado original</li>
					<li>
						Debe conservar todas las etiquetas, etiquetas de precio y embalaje
						original
					</li>
					<li>El producto no debe haber sido personalizado o modificado</li>
					<li>
						Debe acompañarse del comprobante de compra (factura o albarán)
					</li>
				</ul>

				<TypographyH2 className="mt-8">Excepciones</TypographyH2>
				<TypographyP>
					Los siguientes productos no pueden devolverse:
				</TypographyP>
				<ul className="my-6 ml-6 list-disc space-y-2">
					<li>
						Productos personalizados o hechos específicamente para ti
						(commissions o encargos especiales)
					</li>
					<li>
						Productos domésticos (ropa de cama, textiles domésticos) por razones
						de higiene
					</li>
					<li>Productos marcados como "venta final" o "rebajas"</li>
				</ul>

				<TypographyH2 className="mt-8">Proceso de Devolución</TypographyH2>
				<TypographyP>
					Para iniciar una devolución, sigue estos pasos:
				</TypographyP>
				<ul className="my-6 ml-6 list-disc space-y-2">
					<li>
						Contacta con nosotros a través de nuestro formulario de contacto o
						envía un email a devoluciones@gretaspatchwork.com
					</li>
					<li>Proporciona tu número de pedido y el motivo de la devolución</li>
					<li>
						Te enviaremos instrucciones detalladas y una etiqueta de devolución
						prepagada (para Península)
					</li>
					<li>Empaqueta el producto de forma segura</li>
					<li>
						Entrega el paquete en la oficina de correos más cercana o solicita
						recojo a domicilio
					</li>
				</ul>

				<TypographyH2 className="mt-8">Gastos de Devolución</TypographyH2>
				<TypographyP>
					Los gastos de devolución son gratuitos para pedidos dentro de la
					Península Ibérica. Para envíos a Baleares, Canarias, Ceuta y Melilla,
					y para envíos a otros países europeos, los gastos de devolución
					correrán a cargo del cliente salvo en casos de error por nuestra parte
					o producto defectuoso.
				</TypographyP>

				<TypographyH2 className="mt-8">Reembolsos</TypographyH2>
				<TypographyP>
					Una vez recibido e inspeccionado el producto devuelto, procesaremos tu
					reembolso en un plazo de <strong>5-7 días hábiles</strong>. El
					reembolso se realizará mediante el mismo método de pago utilizado en
					la compra original.
				</TypographyP>
				<TypographyP className="mt-4">
					Recibirás un email de confirmación cuando tu reembolso haya sido
					procesado. El tiempo exacto para que el importe aparezca en tu cuenta
					puede variar según el método de pago y la entidad bancaria.
				</TypographyP>

				<TypographyH2 className="mt-8">
					Productos Defectuosos o Erróneos
				</TypographyH2>
				<TypographyP>
					Si recibes un producto defectuoso, erróneo o dañado durante el envío,
					por favor contacta inmediatamente con nosotros. Tendremos todo el
					gusto en:
				</TypographyP>
				<ul className="my-6 ml-6 list-disc space-y-2">
					<li>Sustituir el producto por otro en perfectas condiciones</li>
					<li>Reintegrarte el importe completo</li>
					<li>Asumir todos los gastos de devolución</li>
				</ul>

				<TypographyH2 className="mt-8">Cambios</TypographyH2>
				<TypographyP>
					Si deseas cambiar un producto por otro de diferente talla, color o
					modelo, el proceso es el mismo que para una devolución. Una vez
					recibido el producto original, procesaremos el envío del nuevo
					producto. Si existe diferencia de precio, te reintegraré o cobrará la
					diferencia según corresponda.
				</TypographyP>

				<TypographyH2 className="mt-8">Información de Contacto</TypographyH2>
				<TypographyP>
					Para cualquier consulta sobre devoluciones, puedes contactarnos a
					través de nuestro formulario de contacto o enviar un email a:
				</TypographyP>
				<TypographyP className="mt-4 font-semibold">
					devoluciones@gretaspatchwork.com
				</TypographyP>

				<TypographyP className="mt-8 text-sm text-muted-foreground">
					Última actualización: Febrero 2026
				</TypographyP>
			</div>
		</main>
	)
}
